import { useCallback, useEffect, useState } from "react";
import { api, flash, getToken } from "../lib/api";
import { dayLabel } from "../lib/time";
import { addDays } from "../lib/days";
import { monthName } from "../lib/hours";
import { ClientPicker } from "../components/ClientPicker";
import { Preview } from "../components/ExportPreview";

const wd = (d) => new Date(`${d}T00:00:00Z`).getUTCDay();
const dm = (d) => dayLabel(d).slice(4);
function presets(today) {
  const mon = addDays(today, -((wd(today) + 6) % 7));
  const lm = new Date(`${today.slice(0, 7)}-01T00:00:00Z`); lm.setUTCMonth(lm.getUTCMonth() - 1);
  const lmIso = lm.toISOString().slice(0, 7);
  const span = (a, b) => (a.slice(5, 7) === b.slice(5, 7) ? `${Number(a.slice(8))} to ${dm(b)}` : `${dm(a)} to ${dm(b)}`);
  return [["today", "Today", dayLabel(today)], ["yesterday", "Yesterday", dayLabel(addDays(today, -1))],
    ["this_week", "This week", span(mon, today)], ["last_week", "Last week", span(addDays(mon, -7), addDays(mon, -1))],
    ["this_month", "This month", span(`${today.slice(0, 7)}-01`, today)], ["last_month", "Last month", `${monthName(lmIso).slice(0, 3)} ${lmIso.slice(0, 4)}`]];
}

function query(f) {
  const q = new URLSearchParams();
  if (f.preset) q.set("preset", f.preset); else { q.set("from", f.from); q.set("to", f.to); }
  ["team", "type", "person"].forEach((k) => f[k] !== "all" && q.set(k, f[k]));
  if (f.clients.length) q.set("clients", f.clients.join(","));
  return q.toString();
}

function Seg({ label, value, opts, onChange, testid }) {
  return (
    <div><div className="lbl">{label}</div>
      <div className="xseg" role="radiogroup" aria-label={label}>{opts.map(([v, l]) => (
        <button key={v} role="radio" aria-checked={value === v} className={value === v ? "on" : ""} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}>{l}</button>))}</div></div>
  );
}

export default function Export({ me }) {
  const [f, setF] = useState({ preset: "this_month", from: "", to: "", team: "all", type: "all", person: "all", clients: [] });
  const [pv, setPv] = useState(null);
  const [err, setErr] = useState(null);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!f.preset && (!f.from || !f.to)) return;
    const r = await api(`/reports/preview?${query(f)}`);
    if (r.ok) { setPv(r.data); setErr(null); } else { setPv(null); setErr(r.error.message); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api("/clients").then((r) => r.ok && setClients(r.data.items)); }, []);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  async function download() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export?${query(f)}`, { headers: { Authorization: `Bearer ${getToken()}` }, credentials: "include" });
      if (!res.ok) { const j = await res.json().catch(() => null); return flash(j?.error?.code === "NO_ROWS" ? j.error.message : "The file could not be built. Try again, or narrow the period."); }
      const name = /filename="?([^";]+)"?/.exec(res.headers.get("Content-Disposition") || "")?.[1] || pv.fileName;
      const url = URL.createObjectURL(await res.blob());
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      flash(`${name} is on its way.`);
    } catch { flash("The file could not be built. Try again, or narrow the period."); } finally { setBusy(false); }
  }
  const people = pv ? [...pv.loggers.design, ...pv.loggers.video] : [];
  const empty = pv && pv.logCount === 0;
  const range = pv?.filter?.from ? `${dayLabel(pv.filter.from)} to ${dayLabel(pv.filter.to)}` : "this period";
  return (
    <div data-testid="export-page">
      <div className="xhead">
        <div><h1 className="stackh"><span className="hl-ink">HOURS</span> <span className="hl-mari">TO</span> <span className="hl-g">EXCEL</span></h1>
          <p className="xintro">Pick a period, narrow it down, check the preview, download. Only time logged inside the period lands in the file: hours per client split into Design and Video, revision hours, who worked on what, and every timer stretch.</p></div>
        <div className="xwho"><b>{me.user.name}</b> exporting<div className="hint0">{me.user.designation}</div>
          <div className="xwho-tags"><span className="tag">{me.user.role}</span><span className="tag tag-outline">owner</span></div></div>
      </div>
      <div className="xsec-h"><span className="lbl">Period</span>{pv && <span className="hint0">Data from {dayLabel(pv.dataStart)}</span>}</div>
      <div className="xperiods">
        {presets(me.today).map(([v, l, sub]) => (
          <button key={v} className={f.preset === v ? "on" : ""} aria-pressed={f.preset === v} onClick={() => setF({ ...f, preset: v })} data-testid={`preset-${v}`}><b>{l}</b><span className="num">{sub}</span></button>))}
        <button className={!f.preset ? "on" : ""} aria-pressed={!f.preset} onClick={() => setF({ ...f, preset: "" })} data-testid="preset-custom"><b>Custom</b><span className="num">Pick dates</span></button>
      </div>
      {!f.preset && <div className="xdates"><label><span className="lbl">From</span><input className="fld" type="date" value={f.from} onChange={(e) => set("from")(e.target.value)} data-testid="export-from" /></label>
        <label><span className="lbl">To</span><input className="fld" type="date" value={f.to} max={me.today} onChange={(e) => set("to")(e.target.value)} data-testid="export-to" /></label></div>}
      {pv && <p className="xrange" data-testid="export-range">▣ <b>{range}</b></p>}
      <div className="xsec-h rule"><span className="lbl">Filters</span></div>
      <div className="xfilters">
        <ClientPicker clients={clients} value={f.clients} hours={pv?.pickerHours || {}} onChange={set("clients")} />
        <Seg label="Client type" value={f.type} onChange={set("type")} testid="export-type" opts={[["all", "All"], ["retainer", "Retainer"], ["project", "Project"], ["internal", "Internal"]]} />
        <div className="xrow2"><Seg label="Team" value={f.team} onChange={set("team")} testid="export-team" opts={[["all", "All"], ["design", "Design"], ["video", "Video"]]} />
          <div><div className="lbl">Person</div>
            <select className="fld" value={f.person} onChange={(e) => set("person")(e.target.value)} data-testid="export-person"><option value="all">Everyone</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div></div>
      </div>
      {err && <p className="err" role="alert" data-testid="export-error">{err}</p>}
      {pv && (
        <section className="xprev" data-testid="export-preview">
          <div className="xsec-h rule"><span className="lbl">Preview</span><span className="hint0">Exactly what goes in the file. Updates as you change filters.</span></div>
          {!empty && <Preview pv={pv} />}
          {empty && <div className="xempty" data-testid="export-empty"><b>No hours in this slice.</b>
            <p className="hint">Nothing was logged for these filters, {range}. Widen the period or clear a filter.</p></div>}
          <div className="xfile">
            <div><div className="lbl">The file</div><p className="xname mono" data-testid="export-filename">{pv.fileName}</p>
              <p className="hint">{pv.logCount} timer {pv.logCount === 1 ? "stretch" : "stretches"}. Opens in Excel, Numbers or Google Sheets.</p></div>
            <button className="btn-primary" disabled={empty || busy} aria-busy={busy} aria-describedby={empty ? "xdl-why" : undefined} onClick={download}
              data-testid="export-download">{busy ? "Preparing the file" : "Download .xlsx"}</button>
            {empty && <p id="xdl-why" className="hint" data-testid="export-download-reason">Nothing logged for these filters. Widen the period or clear a filter.</p>}
          </div>
        </section>
      )}
    </div>
  );
}
